import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    Typography,
    Box,
    IconButton,
    Stack,
    Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TextCopyChip from "../../chips/text-copy-chip";
import { useGenerateCalendarSubscriptionUrl } from "../../../store";

interface SyncDialogProps {
    open: boolean;
    onClose: () => void;
}

/**
 * Dialog for generating and copying an Outlook calendar subscription URL.
 * The URL is fetched automatically whenever the dialog opens.
 */
const SyncDialog: React.FC<SyncDialogProps> = ({ open, onClose }) => {

    // Tracks whether the URL copy action succeeded so we can show feedback.
    const [url, setUrl] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<boolean>(false)

    const onSuccess = (url: string | null) => {
        if (!url) {
            onError("Missing Url")
            return
        }

        setIsLoading(false)
        setUrl(url)
    }

    const onError = (err: unknown) => {
        console.error("Failed to get url", err)
        setIsLoading(false)
        setError(true)
        setUrl(null)
    }

    const { mutate: urlData } = useGenerateCalendarSubscriptionUrl(onSuccess, onError)

    const generateUrl = () => {
        setIsLoading(true)
        urlData()
    }

    // Reset dialog state and fetch a fresh URL whenever the dialog opens.
    useEffect(() => {
        if (!open) {
            return
        }

        setError(false)
        setUrl(null)
        generateUrl()
    }, [open])

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogContent dividers sx={{ p: 1, position: "relative" }}>
                    <IconButton
                        onClick={onClose}
                        aria-label="close"
                        sx={{
                            position: "absolute",
                            top: "10px",
                            right: "10px",
                            color: (theme) => theme.palette.grey[500],
                            width: "25px",
                            height: "25px",
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Stack sx={{ px: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 600, pr: 4 }}>
                            Microsoft Outlook Calendar Sync
                        </Typography>
                        <Stack >
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Subscribe to your Clear Call meetings in your Outlook calendar.
                            </Typography>
                        </Stack>
                        <Box
                            sx={{
                                border: 1,
                                borderColor: "primary.light",
                                p: 2,
                                pt: 1.5,
                                my: 1,
                                borderRadius: 1.5,
                            }}
                        >
                            <Stack spacing={1.25}>
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    <Stack
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: "50%",
                                            bgcolor: "primary.main",
                                            color: "primary.contrastText",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontFamily: "'Trebuchet MS', 'Segoe UI', sans-serif",
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                            flexShrink: 0,
                                        }}
                                    >
                                        1
                                    </Stack>
                                    <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.45 }}>
                                        In Outlook select <Box component="span" sx={{ fontWeight: 700 }}>Add calendar</Box>.
                                    </Typography>
                                </Stack>

                                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                                    <Stack
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: "50%",
                                            bgcolor: "primary.main",
                                            color: "primary.contrastText",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontFamily: "'Trebuchet MS', 'Segoe UI', sans-serif",
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                            flexShrink: 0,
                                        }}
                                    >
                                        2
                                    </Stack>
                                    <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.45 }}>
                                            Choose <Box component="span" sx={{ fontWeight: 700 }}>Subscribe from web</Box> and paste the URL below.
                                        </Typography>

                                        {isLoading && !url && (
                                            <Skeleton
                                                variant="rectangular"
                                                width="100%"
                                                height={28}
                                                sx={{
                                                    bgcolor: "grey.100",
                                                    borderRadius: 1.25,
                                                    mt: 0.5,
                                                }}
                                            />
                                        )}

                                        {url && <TextCopyChip text={url} />}
                                    </Stack>
                                </Stack>

                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    <Stack
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: "50%",
                                            bgcolor: "primary.main",
                                            color: "primary.contrastText",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontFamily: "'Trebuchet MS', 'Segoe UI', sans-serif",
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                            flexShrink: 0,
                                        }}
                                    >
                                        3
                                    </Stack>
                                    <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.45 }}>
                                        Name your calendar and click <Box component="span" sx={{ fontWeight: 700 }}>Import</Box>.
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>
                        {error && (
                            <Typography variant="body2" color="error" gutterBottom>
                                Failed to generate URL
                            </Typography>
                        )}
                        <Stack />
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SyncDialog;